import React from "react";
import { createRoot } from 'react-dom/client';

import "semantic-ui-css/semantic.min.css";
import "./index.css";
import './datepicker.css';
import App from "./App";
import * as serviceWorker from "./serviceWorker";
import * as Sentry from "@sentry/react";
import pkg from '../package.json';
const { version } = pkg;

// Headers that may contain credentials/PII and must be scrubbed before sending to Sentry.
const SENTRY_SENSITIVE_HEADERS = [
  'authorization',
  'cookie',
  'set-cookie',
  'x-api-key',
  'x-auth-token',
  'x-amz-security-token',
  'x-amz-date',
  'x-amz-content-sha256',
  'x-amz-target',
  'x-amz-user-agent',
];

const scrubSentryHeaders = (headers) => {
  if (!headers || typeof headers !== 'object') return headers;
  return Object.fromEntries(
    Object.entries(headers).map(([k, v]) =>
      SENTRY_SENSITIVE_HEADERS.includes(String(k).toLowerCase()) ? [k, '[scrubbed]'] : [k, v]
    )
  );
};

const stripSentryQuery = (u) => {
  if (typeof u !== 'string') return u;
  const qIdx = u.indexOf('?');
  return qIdx === -1 ? u : u.slice(0, qIdx);
};

Sentry.init({
  dsn: "https://c9bf7c32a9c26c0d0c3789e3fc650a38@o4509452054233088.ingest.de.sentry.io/4509452056133712",
  release: `bsportaal@${version}`,
  // Setting this option to true will send default PII data to Sentry.
  // For example, automatic IP address collection on events
  sendDefaultPii: true,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({
      // TODO: Once explicit ignore/mask/block selectors are in place for non-sensitive
      // elements (e.g. labels, headings, navigation), these can be relaxed. For now we
      // mask all text and block all media in replays to prevent form input contents
      // (passwords, JWTs typed in dev consoles, PII in form fields) from leaking.
      maskAllText: true,
      blockAllMedia: true,
    })
  ],
  // Tracing
  tracesSampleRate: 1.0, //  Capture 100% of the transactions
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  // Removed API domain to prevent CORS issues with baggage header
  tracePropagationTargets: ["localhost"],
  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  // Strip credentials/PII from outgoing events. AxiosError objects carry the full
  // request config (Authorization JWT, body, query params) — Sentry must never see these.
  beforeSend(event, hint) {
    try {
      if (event.request) {
        event.request.headers = scrubSentryHeaders(event.request.headers);
        if (event.request.url) event.request.url = stripSentryQuery(event.request.url);
        if (event.request.data !== undefined) event.request.data = '[scrubbed]';
        if (event.request.query_string) event.request.query_string = '[scrubbed]';
      }
      const cfg = hint?.originalException?.config;
      if (cfg && typeof cfg === 'object') {
        if (cfg.headers) cfg.headers = scrubSentryHeaders(cfg.headers);
        if (cfg.url) cfg.url = stripSentryQuery(cfg.url);
        if (cfg.data !== undefined) cfg.data = '[scrubbed]';
        if (cfg.params) cfg.params = '[scrubbed]';
      }
      if (event.extra?.config && typeof event.extra.config === 'object') {
        if (event.extra.config.headers) event.extra.config.headers = scrubSentryHeaders(event.extra.config.headers);
        if (event.extra.config.url) event.extra.config.url = stripSentryQuery(event.extra.config.url);
        if (event.extra.config.data !== undefined) event.extra.config.data = '[scrubbed]';
      }
    } catch (_scrubErr) {
      // Never block event delivery on scrub errors — fall through.
    }
    return event;
  },
});

// Reload only after the new service worker has actually taken control. The old
// code reloaded synchronously right after postMessage(SKIP_WAITING), which races
// the worker's async skipWaiting()/clients.claim() — the reload usually landed
// still controlled by the old worker, so the new version did not take effect.
// Reloading on `controllerchange` waits for the new worker to claim the page.
// The flag scopes the reload to a user-confirmed update, so the first-ever
// install (where clients.claim() also fires controllerchange) does not reload.
let updateReloadPending = false;
if ("serviceWorker" in navigator) {
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (!updateReloadPending) return;
    updateReloadPending = false;
    window.location.reload();
  });
}

const configuration = {
  onUpdate: (registration) => {
    if (registration && registration.waiting) {
      if (
        window.confirm(
          `
          New version of portal is available. Refresh your page now? \n
          Nuwe weergawe van die portaal is beskikbaar. Verfris jou bladsy nou?
          `
        )
      ) {
        updateReloadPending = true;
        registration.waiting.postMessage({ type: "SKIP_WAITING" });
        // The reload now fires on the controllerchange handler above, once the
        // new worker has called skipWaiting() and claimed clients.
      }
    }
  },
};

const app = (<App />);

const container = document.getElementById("root");
const root = createRoot(container);
root.render(app);
serviceWorker.register(configuration);
